import { MenuItem, Recipe, Size } from '@/models/menuModels';
import ApiHelper from '@/services/ApiHelper';
import { LineItem } from '@/models/cartModels';
import { mapMenuItemToLineItem } from '@/services/orderService';
import { getLocalStorage, localStorageEnum, replaceLocalStorage } from '@/services/localStorageService';
import { ActiveBundleDetails, Bundle, BundleDTO, BundleGroup } from '@/models/bundleModels';
import { mapBundleGroupItemsToMenuItems } from '@/services/bundleService';
import { ActionContext } from 'vuex';
import { StoreLocation } from '@/models/locationModels';
import { createSlug } from '@/services/stringService';

export interface BundleState {
    activeBundle: Bundle | null;
    activeGroupIndex: number | null;
    activeGroup: BundleGroup | null;
    activeGroupItems: ReadonlyArray<MenuItem> | null;
    bundles: ReadonlyArray<Bundle>;
    bundlesLoaded: boolean;
    cart: ReadonlyArray<LineItem[]>;
    identifier: string;
    activeCouponNumber: string | null;
    valueBundle: Bundle | null;
}

const localBundleState = getLocalStorage(localStorageEnum.bundleState);

if (localBundleState) {
    // remove bundles from state
    localBundleState.bundles = [];
    localBundleState.bundlesLoaded = false;
}

const BundleState: BundleState = localBundleState ?? {
    activeCouponNumber: null,
    activeGroupIndex: null,
    activeGroup: null,
    activeGroupItems: null,
    activeBundle: null,
    valueBundle: null,
    bundles: [],
    bundlesLoaded: false,
    cart: [],
    identifier: '',
};

export default {
    state: BundleState,
    mutations: {
        SET_BUNDLES(state: BundleState, bundles: Bundle[]) {
            state.bundles = bundles;
            state.bundlesLoaded = true;
        },
        SET_ACTIVE_BUNDLE(state: BundleState, bundle: Bundle | null) {
            state.activeBundle = bundle;
        },
        ADD_LINE_ITEM_TO_CART(state: BundleState, lineItem: LineItem) {
            if (state.activeGroupIndex === null) {
                return;
            }
            const groupIndex = state.activeGroupIndex;
            const updatedCart = [...state.cart];
            updatedCart[groupIndex] = [...(updatedCart[groupIndex] ?? []), lineItem];
            state.cart = updatedCart;
            replaceLocalStorage(localStorageEnum.bundleState, state);
        },
        CLEAR_BUNDLE_CART(state: BundleState) {
            state.cart = [];
            replaceLocalStorage(localStorageEnum.bundleState, state);
        },
        SET_VALUE_BUNDLE(state: BundleState, bundle: Bundle | null) {
            state.valueBundle = bundle;
        },
        SET_BUNDLE_CART(state: BundleState, cart: any) {
            state.cart = cart;
        },
        REMOVE_ITEM_FROM_GROUP(state: BundleState, data: any) {
            state.cart[data.groupIndex].splice(data.itemIndex, 1);
            replaceLocalStorage(localStorageEnum.bundleState, state);
        },
        SET_ACTIVE_COUPON_NUMBER(state: BundleState, couponNumber: any) {
            // Normalize to a string so the cart-clear guard compares like types regardless of
            // whether the caller passes a parsed number (bundlePage) or the raw route param string.
            const normalizedCouponNumber = couponNumber != null ? String(couponNumber) : null;
            if (normalizedCouponNumber !== state.activeCouponNumber) {
                state.cart = [];
            }
            state.activeCouponNumber = normalizedCouponNumber;
            state.identifier = normalizedCouponNumber + '-' + Date.now();
            replaceLocalStorage(localStorageEnum.bundleState, state);
        },
        SET_ACTIVE_GROUP_INDEX(state: BundleState, idx: any) {
            state.activeGroupIndex = idx;
        },
        SET_ACTIVE_GROUP(state: BundleState, group: BundleGroup | null) {
            state.activeGroup = group;
        },
        SET_ACTIVE_GROUP_ITEMS(state: BundleState, items: ReadonlyArray<MenuItem> | null) {
            state.activeGroupItems = items;
        },
    },
    actions: {
        initActiveBundle(context: ActionContext<BundleState, any>, payload: ActiveBundleDetails) {
            context.commit('SET_ACTIVE_COUPON_NUMBER', payload.couponNumber);
            context.commit('SET_ACTIVE_GROUP_INDEX', payload.groupIndex);

            const activeBundle: Bundle | null = context.state.bundles.find((b) => parseInt(b.couponNumber) === parseInt(payload.couponNumber)) ?? null;
            context.commit('SET_ACTIVE_BUNDLE', activeBundle ?? null);

            // bundleGroups are sorted in setBundles; group items are sorted in mapBundleGroupItemsToMenuItems
            const activeGroup: BundleGroup | null = activeBundle ? activeBundle.bundleGroups[payload.groupIndex] ?? null : null;
            context.commit('SET_ACTIVE_GROUP', activeGroup);
            context.commit('SET_ACTIVE_GROUP_ITEMS', activeGroup ? activeGroup.items : null);
        },
        async initBundleItem(context: ActionContext<BundleState, any>, payload: ActiveBundleDetails): Promise<void> {
            try {
                context.dispatch('initActiveBundle', payload);

                const activeGroup: BundleGroup | null = context.state.activeGroup;
                const menuItem: MenuItem | null = activeGroup ? activeGroup.items.find((i) => i.itemId === payload.itemId) ?? null : null;
                context.commit('SET_MENU_ITEM', menuItem);

                if (!menuItem || !menuItem?.recipes) {
                    return;
                }
                const menuCategory = context.getters.getLocationMenu?.find((c) => createSlug(c.name) === menuItem.categoryName.toLowerCase());
                context.commit('SET_MENU_CATEGORY', menuCategory ?? null);
                const recipes: ReadonlyArray<Recipe> = menuItem?.recipes;
                context.commit('SET_SELECTED_CRUST', recipes[0]);
                const size: Size | null = recipes ? recipes[0].sizes[0] : null;
                context.commit('SET_SELECTED_SIZE', size);

                if (!size) return;

                const availableTopping = await context.dispatch('setAvailableTopping', { menuItem, recipe: recipes[0], size });
                context.commit('SET_AVAILABLE_TOPPING', availableTopping);

                const lineItem = mapMenuItemToLineItem(menuItem, size, recipes[0], availableTopping);
                context.commit('SET_LINE_ITEM', lineItem);

                context.dispatch('setLineItemDefaults');

                await context.dispatch('updateLineItemPriceAndCal');

                context.dispatch('validateRequiredSelections');
            } catch (error) {
                throw new Error('Failed to initialize value item.');
            }
            return;
        },
        removeItemFromGroup(context: ActionContext<BundleState, any>, data: any) {
            context.commit('REMOVE_ITEM_FROM_GROUP', data);
        },
        resetBundle(context: ActionContext<BundleState, any>) {
            context.commit('CLEAR_BUNDLE_CART');
        },
        addToBundle(context: ActionContext<BundleState, any>) {
            context.commit('ADD_LINE_ITEM_TO_CART', context.getters.getCurrentLineItem);
        },
        async fetchBundles(context: ActionContext<BundleState, any>) {
            const location: StoreLocation = context.getters.getSelectedLocation;
            if (!location) {
                return;
            }

            try {
                const res = await ApiHelper.BlackBrick.get(`/stores/${location.id}/coupons/bundles`);
                context.dispatch('setBundles', res.data);
            } catch (error) {
                console.error('Failed to fetch bundles', error);
            }
            return;
        },
        setBundles(context: ActionContext<BundleState, any>, bundlesDTO: BundleDTO[]) {
            const bundles: Bundle[] = bundlesDTO.map((bundle) => {
                return {
                    name: bundle.name,
                    subText: bundle.sub_text,
                    badgeText: bundle.badge_text,
                    imageUrl: bundle.image_url,
                    couponNumber: String(bundle.coupon_number),
                    isValue: bundle.is_value,
                    valuePrice: bundle.value_price,
                    bundleGroups: bundle.bundle_groups
                        .map((bg) => {
                            const bundleGroup: BundleGroup = {
                                name: bg.name,
                                sort: bg.sort,
                                minRequired: bg.min_required,
                                maxAllowed: bg.max_allowed,
                                displayStyle: bg.display_style,
                                items: mapBundleGroupItemsToMenuItems(context.getters.getLocationMenu, bg.items),
                            };
                            return bundleGroup;
                        })
                        .sort((a, b) => a.sort - b.sort),
                };
            });
            context.commit('SET_BUNDLES', bundles);

            context.commit(
                'SET_VALUE_BUNDLE',
                bundles.find((b) => b.isValue)
            );
        },
        removeBundleFromCart(context: any, identifier: string) {
            const bundleItems = context.rootState.orderModule.cartItems.filter((item: any) => item.bundleIdentifier?.startsWith(identifier));
            const couponNumber = identifier.substring(0, 3);

            const coupon = context.rootState.orderModule.validCoupons.find((c: any) => parseInt(c.code) === parseInt(couponNumber));

            if (coupon) {
                context.dispatch('removeCoupon', coupon, { root: true });
            }

            bundleItems.forEach((item: any) => {
                context.dispatch('deleteCartItem', item, { root: true });
            });
        },
        convertBundleToCartItems(context: any, couponNumber: string) {
            const bundleItems = context.rootState.orderModule.cartItems.filter((item: any) => {
                return item.bundleIdentifier && item.bundleIdentifier.startsWith(couponNumber);
            });

            bundleItems.forEach((item: any) => {
                item.bundleIdentifier = null;
            });
        },
        changeCartBundle(context: any, identifier: string) {
            const bundleItems = context.rootState.orderModule.cartItems.filter((item: any) => {
                return item.bundleIdentifier && item.bundleIdentifier.startsWith(identifier);
            });

            const couponNumber = identifier.substring(0, 3);

            const coupon = context.rootState.orderModule.validCoupons.find((c: any) => parseInt(c.code) === parseInt(couponNumber));

            if (coupon) {
                context.dispatch('removeCoupon', coupon, { root: true });
            }

            const newBundleCart: any[] = [];
            bundleItems.forEach((item: any) => {
                const groupIndex = item.bundleIdentifier.split('--')[1];
                if (!Array.isArray(newBundleCart[groupIndex])) {
                    newBundleCart[groupIndex] = [];
                }
                newBundleCart[groupIndex].push(item);
                context.dispatch('deleteCartItem', item);
            });

            context.commit('SET_BUNDLE_CART', newBundleCart);
        },
        setActiveCouponNumber(context: any, couponNumber: string) {
            context.commit('SET_ACTIVE_COUPON_NUMBER', couponNumber);
        },
    },
    getters: {
        getNonValueBundles(state: BundleState) {
            return state.bundles.filter((b) => !b.isValue);
        },
        getValueBundle(state: BundleState) {
            return state.valueBundle;
        },
        getAllBundles(state: BundleState) {
            return state.bundles;
        },
        getBundleIdentifier(state: BundleState) {
            return state.identifier;
        },
        getActiveBundle(state: BundleState) {
            return state.activeBundle;
        },
        getActiveGroup(state: BundleState) {
            return state.activeGroup;
        },
        getActiveGroupItems(state: BundleState) {
            return state.activeGroupItems;
        },
        getActiveCouponNumber(state: BundleState) {
            return state.activeCouponNumber;
        },
        getActiveGroupIndex(state: BundleState) {
            return state.activeGroupIndex;
        },
        bundlesLoaded(state: BundleState) {
            return state.bundlesLoaded;
        },
        bundleCart(state: BundleState) {
            return state.cart;
        },
        bundleInProgress(state: BundleState) {
            return !!state.activeBundle && state.cart.some((group) => group?.length > 0);
        },
    },
};
